import type { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { sendMail } from 'src/actions/mail';
import { searchForAllCountriesStaticProps } from 'src/actions/searchActions';
import SuccessIcon from 'src/assets/SuccessIcon';
import { NavFooter } from 'src/components/nav/navFooter.component';
import { RecaptchaV2 } from 'src/components/recaptcha-v2/recaptcha-v2';
import { displayToast } from 'src/utils/displayToast';
import { trackCustomEvent } from 'src/utils/trackCustomEvents';
import ErrorIcon from '../assets/ErrorIcon';
import StandardSEOWrapper from '../components/layout/standardSEOWrapper.component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function ContactUs({ countries }: any) {
  const router = useRouter();

  const routeToLastPage = () => {
    router.back();
  };

  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState<null | string>(null);
  const [recaptchaPassed, setRecaptchaPassed] = useState<boolean>(false);

  const isValidEmail = (email: string) => {
    return /\S[^\s@]*@\S+\.\S+/.test(email);
  };

  const handleChange = (event: { target: { value: string } }) => {
    if (!isValidEmail(event.target.value)) {
      setError('Email is invalid');
    } else {
      setError(null);
    }

    setMessage(event.target.value);
  };

  const sendEmail = () => {
    trackCustomEvent('Contact', 'Contact');
    if (error) {
      displayToast('Looks like you provided an invalid email address');
      return;
    }

    if (
      message?.length > 0 &&
      name?.length > 0 &&
      text?.length > 0 &&
      recaptchaPassed
    ) {
      sendMail(message, name, text).then((res) => {
        if (res) {
          displayToast('Message send successfully', 'success', <SuccessIcon />);
          setMessage('');
          setName('');
          setText('');
          setRecaptchaPassed(false);
        } else {
          displayToast(
            'There was a problem sending the email',
            'error',
            <ErrorIcon />
          );
        }
      });
    } else {
      displayToast('All fields must be filled', 'error', <ErrorIcon />);
    }
  };

  const handleName = (e: { target: { value: string } }) => {
    setName(e.target.value);
  };

  const handleText = (e: { target: { value: string } }) => {
    setText(e.target.value);
  };

  const handleRecaptchaChange = (value: string | null) => {
    if (value) {
      setRecaptchaPassed(true);
    }
  };

  const { t } = useTranslation('contact');

  return (
    <div className={`min-h-screen bg-[#E5E5E5] pb-[35px] max-md:pb-[120px]`}>
      <StandardSEOWrapper
        title={t('contact:seo.title')}
        description={t('contact:seo.description')}
      />
      <div
        className={`mx-auto mt-[23px] flex w-full flex-col bg-white md:mt-[46px] md:w-[684px] xl:mt-[37px]`}
      >
        <div className="relative border-b py-[20px]">
          <div
            className={`absolute left-[24.14px] top-[25px] flex items-center opacity-50 md:top-[35px]`}
          >
            <svg width="7" height="11" viewBox="0 0 7 11" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.80104 1.83277C6.08784 1.55138 6.08784 1.09515 5.80104 0.813765C5.51424 0.532375 5.04923 0.532375 4.76243 0.813765L0.35597 5.13703C0.069165 5.41842 0.069165 5.87464 0.35597 6.15603L4.76243 10.4793C5.04923 10.7607 5.51423 10.7607 5.80104 10.4793C6.08784 10.1979 6.08784 9.74168 5.80104 9.46029L1.91389 5.64653L5.80104 1.83277Z"
                fill="black"
              />
            </svg>
            <button
              onClick={routeToLastPage}
              className={`ml-[10px] text-[14px] md:ml-[20px] md:text-[16px]`}
            >
              <p className="hidden md:block">Go Back</p>
              <p className="block md:hidden">Back</p>
            </button>
          </div>
          <p
            className={`text-center  text-[20px] md:text-[28px] xl:text-[30px]`}
          >
            Contact Us
          </p>
        </div>
        <div className={`flex flex-col px-[21px] pt-[26px] md:px-[61px]`}>
          <p>
            We’d love to hear from you! Fill out the form below and tell us
            about...
          </p>
          <div className={`pl-[24px] md:pl-[54px]`}>
            <div className={`flex items-start pt-[10px]`}>
              <p className="mr-[10px]">&#183;</p>
              <p>
                Volunteering (offering your services or inquiring about
                opportunities)
              </p>
            </div>
            <div className={`flex items-start py-[10px]`}>
              <p className="mr-[10px]">&#183;</p>
              <p>Voicing your opinion about The Population Project</p>
            </div>
            <div className={`flex items-start`}>
              <p className="mr-[10px]">&#183;</p>
              <p>
                Providing feedback about the site itself (suggest
                functionalities, report bugs, etc.)
              </p>
            </div>
          </div>
          <div className="pt-[44px]">
            <div className="flex">
              <input
                className={`input mr-[10px] w-full rounded-[5px] border border-[#9E9E9E] placeholder:text-[#8F8F8F] md:mr-[22px]`}
                type="text"
                onChange={handleName}
                value={name}
                placeholder="Full Name"
                alt="Full Name"
              />
              <input
                className="input w-full rounded-[5px] border border-[#9E9E9E] placeholder:text-[#8F8F8F]"
                type="text"
                placeholder="Email"
                value={message}
                onChange={handleChange}
                alt="Email"
              />
            </div>
            {error && (
              <p className="mb-[-10px] pt-[10px] text-center text-red-500">
                Invalid Email
              </p>
            )}
            <textarea
              placeholder="Type your message here..."
              className="mt-[21px] min-h-[313px] w-full resize-none rounded-[5px] border border-[#9E9E9E] placeholder:text-[#8F8F8F]"
              onChange={handleText}
              value={text}
            />

            <div
              className={`mb-[39px] flex w-full flex-col pb-[39px] pt-[21px] md:h-[136px] md:flex-row md:justify-between`}
            >
              <div className={`w-[304px] max-md:mb-[10px] max-md:ml-auto`}>
                <RecaptchaV2 onChange={handleRecaptchaChange} />
              </div>
              <button
                disabled={
                  recaptchaPassed === false ||
                  name?.length === 0 ||
                  text?.length === 0 ||
                  message?.length === 0
                }
                onClick={sendEmail}
                style={{
                  boxShadow: '3px 4px 10px rgba(0, 0, 0, 0.2)',
                }}
                className={`my-auto h-[36px] w-[197px] rounded-[5px] bg-[#409F85] disabled:opacity-30 max-md:ml-auto`}
              >
                <p className="text-white">Send Message</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      <NavFooter className="mb-[-35px] mt-[150px]" />
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const countries = await searchForAllCountriesStaticProps();
  return {
    props: {
      ...(await serverSideTranslations(locale || 'en', [
        'common',
        'navbar',
        'new-homepage',
        'new-navbar',
        'new-footer',
        'contact',
      ])),
      countries: countries || [],
    },
  };
};
